// Browser-local owners only. Registration happens in effects or user actions,
// never during server rendering. Keep transport dependencies out of the shell.
const blockers = new Map<symbol, () => boolean>();
const listeners = new Set<() => void>();

function publish() {
  listeners.forEach((listener) => listener());
}

export function isLessonRefreshBlocked(): boolean {
  return [...blockers.values()].some((isBlocked) => isBlocked());
}

export function subscribeLessonRefreshGuard(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function registerLessonRefreshBlocker(isBlocked: () => boolean) {
  const owner = Symbol("lesson-refresh-owner");
  blockers.set(owner, isBlocked);
  publish();
  return () => {
    if (blockers.delete(owner)) publish();
  };
}

export function holdLessonRefreshUntil<T>(pending: Promise<T>): Promise<T> {
  const release = registerLessonRefreshBlocker(() => true);
  void pending.then(release, release);
  return pending;
}

export function withLessonRefreshProtection<Args extends unknown[], Result>(
  operation: (...args: Args) => Promise<Result>
): (...args: Args) => Promise<Result> {
  return (...args) => {
    const release = registerLessonRefreshBlocker(() => true);
    try {
      const pending = operation(...args);
      void pending.then(release, release);
      return pending;
    } catch (error) {
      release();
      throw error;
    }
  };
}

export function requestAppRefresh(reload: () => void = () => window.location.reload()): boolean {
  // Recheck live transport refs on click, even if React has not rendered the
  // latest connection state yet. Ending a lesson never queues a reload.
  if (isLessonRefreshBlocked()) return false;
  reload();
  return true;
}
